<template>
  <div class="page-fluides">
    <landing-header />

    <main>
      <section class="hero-fluides">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2">
            <span class="badge-reglementaire">Conformité F-Gaz</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Traçabilité des fluides : <br />
              <span class="gradient-text">Zéro risque, 100% tracé</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Automatisez le suivi de vos bouteilles et la manipulation des fluides. Termoflow garantit la cohérence parfaite entre vos stocks réels et vos obligations réglementaires.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
            </div>
          </div>
          
          <div class="lg:w-1/2 w-full">
            <div class="fluid-tracker-ui shadow-2xl">
              <div class="ui-head">Registre des mouvements</div>
              <div class="ui-body">
                <div class="gas-bottle-card">
                  <div class="bottle-icon"><i class="fa-solid fa-bottle-droplet"></i></div>
                  <div class="bottle-info">
                    <span class="id">Bouteille #FR-2024-08</span>
                    <span class="type">Fluide R32</span>
                    <div class="progress-bar"><div class="fill" style="width: 65%"></div></div>
                  </div>
                  <div class="bottle-qty">8.5 / 12 kg</div>
                </div>
                <div class="movement-log">
                  <div class="log-entry">
                    <i class="fa-solid fa-arrow-right-from-bracket text-red-500"></i>
                    <span>-2.5kg (Intervention #902)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center max-w-3xl mx-auto mb-20">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Localisation et suivi en temps réel</h2>
            <p class="text-gray-600">Ne perdez plus jamais une bouteille. Visualisez instantanément la répartition de votre parc de fluides.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div class="loc-tag-card">
              <i class="fa-solid fa-truck"></i>
              <span>Dans les camions</span>
            </div>
            <div class="loc-tag-card">
              <i class="fa-solid fa-warehouse"></i>
              <span>En entrepôt</span>
            </div>
            <div class="loc-tag-card">
              <i class="fa-solid fa-hard-hat"></i>
              <span>Sur chantier</span>
            </div>
            <div class="loc-tag-card">
              <i class="fa-solid fa-recycle"></i>
              <span>En recyclage</span>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-gray-50 border-y border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-center">
            <div class="lg:w-1/2">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">Lien direct entre terrain et CERFA</h2>
              <p class="text-gray-600 mb-8">Chaque gramme de fluide manipulé sur l'application mobile est automatiquement répercuté sur le CERFA de l'intervention et sur le registre des fluides.</p>
              
              <div class="feature-item-icon">
                <div class="f-icon"><i class="fa-solid fa-link"></i></div>
                <div>
                  <h4 class="font-bold">Interopérabilité totale</h4>
                  <p class="text-sm text-gray-500">Lien avec les détecteurs de fuite étalonnés et les adresses de récupération agréées.</p>
                </div>
              </div>

              <div class="feature-item-icon">
                <div class="f-icon"><i class="fa-solid fa-file-shield"></i></div>
                <div>
                  <h4 class="font-bold">Conformité assurée</h4>
                  <p class="text-sm text-gray-500">Génération automatique des documents de traçabilité sans aucune erreur de saisie.</p>
                </div>
              </div>
            </div>
            
            <div class="lg:w-1/2">
               <div class="audit-banner">
                  <i class="fa-solid fa-shield-halved text-5xl mb-6"></i>
                  <h3 class="text-2xl font-bold mb-4">Sérénité en cas d'audit</h3>
                  <p class="opacity-90">Toutes les données sont historisées, archivées et exportables selon les filtres réglementaires. Présentez vos registres de fluides en un clic.</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24">
        <div class="max-w-7xl mx-auto px-4">
          <h2 class="text-center text-3xl font-bold mb-16">Pourquoi choisir Termoflow pour vos fluides ?</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div class="benefit-box-white">
              <div class="benefit-val">0</div>
              <p class="benefit-lab">Erreur déclarative</p>
            </div>
            <div class="benefit-box-white">
              <div class="benefit-val">100%</div>
              <p class="benefit-lab">Traçabilité bouteilles</p>
            </div>
            <div class="benefit-box-white">
              <div class="benefit-val">Zen</div>
              <p class="benefit-lab">Face aux contrôles</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 text-center bg-white">
        <div class="max-w-3xl mx-auto px-4">
          <h2 class="text-4xl font-black text-gray-900 mb-8">Sécurisez votre gestion des fluides</h2>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
             <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
             <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'FluidesPage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* --- UTILS --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* --- HERO --- */
.hero-fluides {
  padding: 10rem 0 6rem;
  background: radial-gradient(circle at top right, #eff6ff, transparent 40%);
}

.badge-reglementaire {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #f0fdf4;
  color: #166534;
  font-weight: 800;
  font-size: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  border: 1px solid #dcfce7;
}

/* --- UI VISUAL --- */
.fluid-tracker-ui {
  background: #1e293b;
  border-radius: 2rem;
  padding: 2rem;
  color: white;
}
.ui-head { font-weight: 900; font-size: 0.8rem; text-transform: uppercase; color: #94a3b8; margin-bottom: 2rem; border-bottom: 1px solid #334155; padding-bottom: 1rem; }
.gas-bottle-card {
  background: #334155;
  padding: 1.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.bottle-icon { font-size: 2rem; color: #38bdf8; }
.bottle-info { flex: 1; }
.bottle-info .id { display: block; font-weight: 800; font-size: 0.9rem; }
.bottle-info .type { font-size: 0.7rem; color: #94a3b8; font-weight: 700; }
.progress-bar { width: 100%; height: 6px; background: #1e293b; border-radius: 3px; margin-top: 0.75rem; }
.progress-bar .fill { height: 100%; background: #38bdf8; border-radius: 3px; }
.bottle-qty { font-family: monospace; font-weight: 800; color: #38bdf8; }

.movement-log { margin-top: 1.5rem; padding-left: 1rem; }
.log-entry { display: flex; align-items: center; gap: 0.75rem; font-size: 0.8rem; font-weight: 700; color: #94a3b8; }

/* --- LOC TAGS --- */
.loc-tag-card {
  background: white;
  padding: 2rem;
  border-radius: 1.5rem;
  text-align: center;
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-weight: 800;
  color: #1e293b;
}
.loc-tag-card i { font-size: 2rem; color: #f97316; }

/* --- AUDIT BANNER --- */
.audit-banner {
  background: #1e293b;
  color: white;
  padding: 4rem;
  border-radius: 3rem;
  text-align: center;
}

/* --- FEATURE ICON --- */
.feature-item-icon { display: flex; gap: 1.5rem; margin-bottom: 2rem; }
.f-icon { width: 3rem; height: 3rem; background: #fff7ed; color: #f97316; border-radius: 0.75rem; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; flex-shrink: 0; }

/* --- BENEFIT BOX --- */
.benefit-box-white {
  background: white;
  padding: 3rem 2rem;
  border-radius: 1.5rem;
  border: 1px solid #f1f5f9;
  text-align: center;
}
.benefit-val { font-size: 3rem; font-weight: 900; color: #f97316; margin-bottom: 0.5rem; }
.benefit-lab { font-weight: 800; color: #475569; }

/* --- BUTTONS --- */
.btn-primary { background: linear-gradient(to right, #f97316, #ef4444); color: white; padding: 1rem 2.5rem; border-radius: 9999px; font-weight: 700; border: none; }
.btn-primary-large { background: #f97316; color: white; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; border: none; font-size: 1.1rem; }
.btn-outline-large { border: 2px solid #e5e7eb; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; color: #1f2937; text-decoration: none; display: inline-block; }

@media (max-width: 1024px) {
  .hero-fluides { text-align: center; padding-top: 8rem; }
  .hero-fluides .flex { flex-direction: column; }
  .fluid-tracker-ui { margin-top: 4rem; }
}
</style>